import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Gift, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function LoyaltySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    loyalty_enabled: false,
    points_per_rupiah: 1000,
    redemption_value: 100,
    min_redeem_points: 10,
    max_redeem_percent: 50,
    point_expiry_days: 0,
  });

  useEffect(() => {
    api.get('/tenant/loyalty-settings')
      .then(r => setSettings(s => ({ ...s, ...r.data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/loyalty-settings', settings);
      toast.success('Pengaturan loyalty berhasil disimpan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('id-ID');

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Gift size={24} /> Pengaturan Loyalty</h1>
        <p className="text-slate-500 text-sm mt-1">Konfigurasi program poin & reward untuk pelanggan</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            Aktifkan Loyalty Program
            <Switch
              checked={settings.loyalty_enabled}
              onCheckedChange={v => setSettings(s => ({ ...s, loyalty_enabled: v }))}
            />
          </CardTitle>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pengaturan Poin</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Poin per Rupiah yang dibelanjakan</Label>
            <p className="text-xs text-slate-500 mb-1">Customer dapat 1 poin setiap belanja Rp {fmt(settings.points_per_rupiah)}</p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">1 poin per Rp</span>
              <Input
                type="number"
                value={settings.points_per_rupiah}
                onChange={e => setSettings(s => ({ ...s, points_per_rupiah: parseInt(e.target.value) || 0 }))}
                className="w-32"
                min={1}
              />
            </div>
          </div>

          <div>
            <Label>Nilai Tukar Poin (Rupiah per poin)</Label>
            <p className="text-xs text-slate-500 mb-1">Setiap 1 poin bernilai Rp {fmt(settings.redemption_value)} saat ditukar</p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">1 poin = Rp</span>
              <Input
                type="number"
                value={settings.redemption_value}
                onChange={e => setSettings(s => ({ ...s, redemption_value: parseInt(e.target.value) || 0 }))}
                className="w-32"
                min={1}
              />
            </div>
          </div>

          <div>
            <Label>Minimal Poin untuk Redeem</Label>
            <Input
              type="number"
              value={settings.min_redeem_points}
              onChange={e => setSettings(s => ({ ...s, min_redeem_points: parseInt(e.target.value) || 0 }))}
              className="w-32"
              min={1}
            />
          </div>

          <div>
            <Label>Maks. Diskon dari Poin (% dari total belanja)</Label>
            <p className="text-xs text-slate-500 mb-1">Batas maksimum diskon poin = {settings.max_redeem_percent}% dari total pesanan</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.max_redeem_percent}
                onChange={e => setSettings(s => ({ ...s, max_redeem_percent: parseInt(e.target.value) || 0 }))}
                className="w-24"
                min={1}
                max={100}
              />
              <span className="text-sm text-slate-500">%</span>
            </div>
          </div>

          <div>
            <Label>Masa Berlaku Poin (hari)</Label>
            <p className="text-xs text-slate-500 mb-1">0 = tidak ada kadaluarsa</p>
            <Input
              type="number"
              value={settings.point_expiry_days}
              onChange={e => setSettings(s => ({ ...s, point_expiry_days: parseInt(e.target.value) || 0 }))}
              className="w-32"
              min={0}
            />
          </div>
        </CardContent>
      </Card>

      {settings.loyalty_enabled && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-sm text-blue-800">
              <strong>Simulasi:</strong> Customer belanja Rp 100.000 → dapat <strong>{Math.floor(100000 / (settings.points_per_rupiah || 1))} poin</strong>. 
              Jika redeem {Math.floor(100000 / (settings.points_per_rupiah || 1))} poin → diskon <strong>Rp {fmt(Math.floor(100000 / (settings.points_per_rupiah || 1)) * settings.redemption_value)}</strong>.
            </p>
          </CardContent>
        </Card>
      )}

      <Button onClick={handleSave} disabled={saving} className="w-full">
        {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
        Simpan Pengaturan
      </Button>
    </div>
  );
}
